import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList } from "@fortawesome/pro-regular-svg-icons/faList";
import axios from "axios";
import { useConfig } from "@peracto/peracto-config";
import EmblaCarousel from "../../utils/EmblaCarousel";

let Icon = () => (
    <div className="flex items-center justify-center w-full py-10">
        <FontAwesomeIcon icon={faList} size="6x" />
    </div>
);

const MenuItemCard = ({
    isEditMode,
    menuItem,
    cardStyle,
    progressIndicatorStyle,
    imageFit,
    fontFamily,
    fontWeight,
    fontColour,
    accentColour,
    arrowColour,
    fallbackImage,
    categoryPath,
    menuItemPath,
    ...restProps
}) => {
    const image =
        menuItem.content?.image ||
        menuItem.image ||
        fallbackImage ||
        "/images/placeholder.png";
    const decodedImage = image?.replaceAll("(", "%28")?.replaceAll(")", "%29");

    const isTrailerPage = menuItemPath === "trailer_pages";

    if (!menuItem.path || menuItem.itemType === "branch") {
        return null;
    }
    let path =
        !menuItem.path.startsWith("http") && menuItemPath[0] !== "/"
            ? `/${menuItem.path}`
            : menuItem.path;

    if (
        menuItem.itemType === "category" &&
        (!isTrailerPage ||
            (isTrailerPage && menuItem?.filteredChildren?.length === 0)) &&
        !menuItem.path.startsWith(`/${categoryPath}/`)
    ) {
        path = `/${categoryPath}${path}`;
    }

    if (
        menuItem.itemType === "category" &&
        isTrailerPage &&
        menuItem?.filteredChildren?.length > 0 &&
        !menuItem.path.startsWith(`/trailers/`)
    ) {
        path = `/trailers${path}`;
    }

    return (
        <a
            style={{
                color: fontColour,
                fontFamily: fontFamily,
                fontWeight: fontWeight,
            }}
            className="!no-underline hover:!underline"
            href={path}
            onClick={(e) => {
                e.preventDefault();
                if (isEditMode) return;

                window.location = path;
            }}
            data-testid="content-menu-items__link"
        >
            <div
                className={`${
                    cardStyle === "square" ? "aspect-square" : "aspect-[3/4]"
                } w-full relative mb-[15px] border border-brand-grey`}
                {...restProps}
            >
                <img
                    alt={menuItem.linkText}
                    src={decodedImage}
                    className={`${
                        imageFit === "cover" ? "object-cover" : "object-contain"
                    } absolute top-0 left-0 w-full h-full`}
                    data-testid="content-menu-items__image"
                />
            </div>
            <p
                style={{ fontFamily: fontFamily }}
                className="text-[18px] leading-[16px] mb-[5px]"
                data-testid="content-menu-items__link-text"
            >
                {menuItem.linkText}
            </p>
        </a>
    );
};

const MenuItemsCarouselComponent = ({
    state: {
        menuItemPath,
        cardStyle,
        progressIndicatorStyle,
        imageFit,
        fontFamily,
        fontWeight,
        fontColour,
        accentColour,
        arrowColour,
        fallbackImage,
        categoryPath = "category",
    },
    isEditMode,
}) => {
    const config = useConfig();
    const API_URL = config.get("api");
    const [menuItemData, setMenuItemData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState(false);

    useEffect(() => {
        if (menuItemPath) {
            const loadMenuItems = async () => {
                setLoadingError(false);
                const path =
                    menuItemPath[0] === "/"
                        ? menuItemPath.substr(1)
                        : menuItemPath;

                const menuUrl =
                    menuItemPath === "trailer_pages"
                        ? `${API_URL}/menus/trailer_pages`
                        : `${API_URL}/menu-items-children-by-path/${path}`;
                try {
                    axios
                        .get(menuUrl)
                        .then((res) => {
                            const data = res.data
                                ? menuItemPath === "trailer_pages"
                                    ? res.data["menuItems"]
                                    : res.data["hydra:member"]
                                : [];

                            if (data) {
                                setMenuItemData(data);
                                setIsLoading(false);
                            }
                        })
                        .catch((error) => {
                            setLoadingError(true);
                            return Promise.reject(error);
                        });
                } catch (e) {
                    console.error(e);
                    setLoadingError(true);
                }
            };

            loadMenuItems();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuItemPath]);

    if (loadingError) {
        // this has padding so the element is at least clickable so it can be selected and edited
        return <div className="w-full py-10"></div>;
    }

    const inStorefront = !window?.location?.pathname.includes("/content/edit/");

    return menuItemPath ? (
        <EmblaCarousel
            accentColour={accentColour}
            arrowColour={arrowColour}
            progressIndicatorStyle={progressIndicatorStyle}
        >
            {menuItemData?.length > 0 > 0
                ? menuItemData.map((menuItem, index) => (
                      <div
                          key={menuItem?.slug || `menuItem_${index}`}
                          className="flex-[0_0_235px]"
                      >
                          <MenuItemCard
                              isEditMode={isEditMode}
                              menuItem={menuItem}
                              cardStyle={cardStyle}
                              imageFit={imageFit}
                              fontFamily={fontFamily}
                              fontWeight={fontWeight}
                              fontColour={fontColour}
                              accentColour={accentColour}
                              arrowColour={arrowColour}
                              fallbackImage={fallbackImage}
                              categoryPath={categoryPath}
                              menuItemPath={menuItemPath}
                          />
                      </div>
                  ))
                : !isLoading && "No Menu Items"}
        </EmblaCarousel>
    ) : inStorefront ? (
        <></>
    ) : (
        <Icon />
    );
};

export default MenuItemsCarouselComponent;
